import React, { useEffect } from 'react';
/**
 * `DateInput` is a culture-aware date picker component built on
 * `react-datepicker`.
 *
 * Responsibilities:
 * - Formats and localises the date picker based on `culture`.
 * - Supports reading/writing dates as a custom JSON string when `useJson` is
 *   enabled (see `ClientDateHandler`).
 * - Optionally normalises the time portion ("forceTimeReset") to avoid
 *   midnight edge cases.
 * - Switches to the datepicker portal UI below `displayMinWidth` for improved
 *   mobile usability.
 * - Optionally renders a popover help icon and triggers `loadPopovers`.
 */
import DatePicker, { registerLocale } from 'react-datepicker';
import MediaQuery from 'react-responsive';
import 'react-datepicker/dist/react-datepicker.css';
import ClientDateHandler from '../ClientDateHandler/ClientDateHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { DateInputProps } from './DateInput.types';
import InputLabel from '../InputLabel/InputLabel';
import { loadPopovers } from '../Popovers/Popovers';
import { de, enGB, enUS } from 'date-fns/locale';
import { getDateFormatString, getPlaceHolderFormatString } from '../Formatter';

/** An input component specifically designed to select or type dates on a calendar that is responsive based on a width break */
const DateInput = ({
  name,
  value,
  error,
  label,
  haslabel,
  placeholder,
  helpStyle,
  helpContent,
  readOnly,
  disabled,
  forceTimeReset,
  useJson = false,
  help,
  displayMinWidth = 400,
  minimumDate,
  maximumDate,
  culture,
  onDateChanged,
  onCalendarClose
}: DateInputProps): JSX.Element => {
  useEffect(() => {
    loadPopovers();
    if (readOnly) return;
    if (haslabel) return;

    if (forceTimeReset) {
      let localDate: Date | undefined = undefined;
      if (useJson && typeof value === 'string')
        localDate = getDateFromJSON(value);
      else localDate = value as unknown as Date;
      if (localDate !== undefined) {
        const updatedDate = updateTime(localDate);

        const shouldUpdate = updatedDate != localDate || localDate == null;
        if (shouldUpdate) {
          callOnChange(updatedDate);
        }
      }
    }
  }, []);

  const dateFormat = getDateFormatString(culture);
  const placeHolderText = placeholder ?? getPlaceHolderFormatString(culture);

  const updateTime = (date: Date) => {
    if (date != null && typeof date == 'object') {
      const hours = date.getHours();
      if (hours === 0) {
        date.setHours(12); // set to midday ..
      }
    }

    return date;
  };

  const callOnChange = (date: Date) => {
    let changeEvent = null;
    if (useJson == true) {
      const dateJson = setDateToJSON(date);
      changeEvent = { target: { value: dateJson } };
    } else {
      changeEvent = { target: { value: date } };
    }

    onDateChanged(changeEvent);
  };

  const onDateChange = (date: Date) => {
    if (forceTimeReset) {
      date = updateTime(date);
    }

    callOnChange(date);
  };

  const onDateCalendarClose = () => {
    onCalendarClose && onCalendarClose();
  };

  const getDateFromJSON = (jsonText: string): Date | undefined => {
    const cdh = new ClientDateHandler();
    cdh.parseJSON(jsonText);

    const date = cdh.getLocalDate();
    return date;
  };

  const setDateToJSON = (date: Date) => {
    const cdh = new ClientDateHandler();
    cdh.setDateValue(false, date);

    const json = cdh.getJSON();
    return json;
  };

  const createLabel = (): JSX.Element => {
    if (label !== undefined) {
      return <InputLabel labelText={label} name={name} />;
    }
    return <></>;
  };

  //Old return logic
  let date: undefined | Date = undefined;
  if (useJson) {
    date = getDateFromJSON(value as string);
  } else date = value as unknown as Date;

  let wrapperClass = 'form-group';
  if (error && error.length > 0) wrapperClass += ' has-error';

  registerLocale('de', de);
  registerLocale('enGB', enGB);
  registerLocale('enUS', enUS);

  let controlLanguage = 'enGB';
  let today = 'Today';
  if (culture?.toLowerCase() === 'de-de') {
    controlLanguage = 'de';
    today = 'Heute';
  }

  if (culture?.toLowerCase() === 'en-us') {
    controlLanguage = 'enUS';
  }

  const dateControl = (
    <>
      <div className='field date input-group small'>
        <MediaQuery maxWidth={displayMinWidth}>
          <>
            <DatePicker
              id={name}
              name={name}
              className='form-control'
              todayButton={today}
              placeholderText={placeHolderText}
              selected={date}
              onChange={onDateChange}
              readOnly={readOnly}
              disabled={disabled}
              minDate={minimumDate}
              maxDate={maximumDate}
              dateFormat={dateFormat}
              locale={controlLanguage}
              withPortal
              autoComplete='off'
              aria-describedby='button-addon1'
              onCalendarClose={onDateCalendarClose}
            />
          </>
        </MediaQuery>
        <MediaQuery minWidth={displayMinWidth + 1}>
          <>
            <DatePicker
              id={name}
              name={name}
              className='form-control'
              dateFormat={dateFormat}
              todayButton={today}
              placeholderText={placeHolderText}
              selected={date}
              onChange={onDateChange}
              readOnly={readOnly}
              disabled={disabled}
              minDate={minimumDate}
              maxDate={maximumDate}
              locale={controlLanguage}
              autoComplete='off'
              aria-describedby='button-addon1'
              onCalendarClose={onDateCalendarClose}
            />
          </>
        </MediaQuery>
      </div>
    </>
  );

  let controlClasses = 'field date input-group';
  if (readOnly) controlClasses += ' readOnly';

  let appendBlock = <></>;
  if (help) {
    appendBlock = (
      <a
        id='button-addon1'
        className={`btn btn-sm btn-primary ${helpStyle}`}
        title={label}
        data-toggle='popover'
        data-trigger='focus'
        data-html='true'
        aria-label='help-block'
        data-content={helpContent}
        tabIndex={0}>
        <FontAwesomeIcon icon={faQuestion} />
      </a>
    );
  }

  const labelControl = createLabel();

  return (
    <div className={wrapperClass}>
      {labelControl}
      <div className={controlClasses}>
        {dateControl}
        {appendBlock}
      </div>
      <div className='input'>{error}</div>
    </div>
  );
};

export default DateInput;
